
/**
 * Converts an array of objects to CSV format
 * @param data Array of objects to convert
 * @param fields Optional list of fields to include (uses all if not specified)
 * @returns CSV formatted string
 */
export const objectsToCSV = <T extends Record<string, any>>(
  data: T[], 
  fields?: (keyof T)[]
): string => {
  if (!data || data.length === 0) return '';
  
  // Determine which fields to include
  const headers = fields || Object.keys(data[0]) as (keyof T)[];
  
  // Create the CSV header row
  const csvRows = [headers.map(header => `"${String(header)}"`).join(',')];
  
  // Create data rows
  for (const row of data) {
    const values = headers.map(header => {
      const value = row[header];
      // Handle different data types appropriately
      if (value === null || value === undefined) return '""';
      if (Object.prototype.toString.call(value) === '[object Date]') return `"${(value as Date).toISOString()}"`;
      if (typeof value === 'object') return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
      return `"${String(value).replace(/"/g, '""')}"`;
    });
    csvRows.push(values.join(','));
  }
  
  return csvRows.join('\n');
};

/**
 * Downloads data as a CSV file
 * @param data The CSV data as a string
 * @param filename The name of the file to download
 */
export const downloadCSV = (data: string, filename: string): void => {
  // Create a blob with the data
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
  
  // Create a download link
  const link = document.createElement('a');
  
  // Support for browsers with URL.createObjectURL
  if (window.URL && window.URL.createObjectURL) {
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};
